import { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
  Link,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Lock,
  Badge,
  Business,
  School,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "../hooks/useAuth0";
import { apiService } from "../services/api"; // ✅ Import from api.js
import api from "../services/api";

export default function Signup() {
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "",
    // Provider/Researcher specific fields
    npiNumber: "",
    medicalLicense: "",
    licenseState: "",
    institution: "",
    specialization: "",
    yearsExperience: "",
    // Practice association (Provider role only)
    practiceId: null,
    practiceName: "",
    practiceAddress: "",
    practiceCity: "",
    practiceState: "",
    practiceZip: "",
    practicePhone: "",
  });

  const [practiceOptions, setPracticeOptions] = useState([]);
  const [practiceInputValue, setPracticeInputValue] = useState("");
  const [searchingPractices, setSearchingPractices] = useState(false);

  // Debounced practice search as the provider types
  useEffect(() => {
    if (formData.role !== "Provider" || !practiceInputValue.trim()) {
      setPracticeOptions([]);
      return;
    }
    setSearchingPractices(true);
    const timeout = setTimeout(async () => {
      try {
        const res = await api.get("/v1/organizations/search", {
          params: { q: practiceInputValue },
        });
        setPracticeOptions(res.data || []);
      } catch (err) {
        console.error("Practice search failed:", err);
        setPracticeOptions([]);
      } finally {
        setSearchingPractices(false);
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [practiceInputValue, formData.role]);

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const steps = ["Account Info", "Role Selection", "Verification"];

  const roles = [
    {
      value: "Patient",
      label: "Patient",
      description: "Access your health records and find clinical trials",
      icon: <Person />,
      color: "primary",
      requiresVerification: false,
    },
    {
      value: "Provider",
      label: "Healthcare Provider",
      description: "Manage patient records and match patients to trials",
      icon: <Badge />,
      color: "success",
      requiresVerification: true,
    },
    {
      value: "Researcher",
      label: "Clinical Researcher",
      description: "Create and manage clinical trials",
      icon: <School />,
      color: "info",
      requiresVerification: true,
    },
  ];

  const usStates = [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ];

  // Validation functions
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special char
    const re =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d])[A-Za-z\d\W]{8,}$/;
    return re.test(password);
  };

  const validateNPI = (npi) => {
    // NPI is exactly 10 digits
    return /^\d{10}$/.test(npi);
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Account Info validation
      if (!formData.firstName.trim()) {
        newErrors.firstName = "First name is required";
      }
      if (!formData.lastName.trim()) {
        newErrors.lastName = "Last name is required";
      }
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!validateEmail(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (!validatePassword(formData.password)) {
        newErrors.password =
          "Password must be at least 8 characters with uppercase, lowercase, number, and special character";
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    if (step === 1) {
      // Role selection validation
      if (!formData.role) {
        newErrors.role = "Please select a role";
      }
    }

    if (step === 2) {
      // Verification validation (for Provider/Researcher only)
      if (formData.role === "Provider") {
        if (!formData.npiNumber.trim()) {
          newErrors.npiNumber = "NPI number is required";
        } else if (!validateNPI(formData.npiNumber)) {
          newErrors.npiNumber = "NPI must be exactly 10 digits";
        }
        if (!formData.medicalLicense.trim()) {
          newErrors.medicalLicense = "Medical license number is required";
        }
        if (!formData.licenseState) {
          newErrors.licenseState = "License state is required";
        }
        if (!formData.specialization.trim()) {
          newErrors.specialization = "Specialization is required";
        }
        if (!formData.practiceName.trim()) {
          newErrors.practiceName = "Please select or enter your practice";
        }
      }

      if (formData.role === "Researcher") {
        if (!formData.institution.trim()) {
          newErrors.institution = "Institution/Organization is required";
        }
        if (!formData.specialization.trim()) {
          newErrors.specialization = "Research area is required";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setLoading(true);
    setSubmitError("");

    try {
      // ✅ Use apiService from api.js
      const data = await apiService.signupUser({
        email: formData.email,
        password: formData.password,
        given_name: formData.firstName,
        family_name: formData.lastName,
        name: `${formData.firstName} ${formData.lastName}`,
        user_metadata: {
          role: formData.role,
          verificationStatus:
            formData.role === "Patient" ? "verified" : "pending",
          // Provider/Researcher metadata
          ...(formData.role === "Provider" && {
            npiNumber: formData.npiNumber,
            medicalLicense: formData.medicalLicense,
            licenseState: formData.licenseState,
            specialization: formData.specialization,
            ...(formData.practiceId
              ? { practiceId: formData.practiceId }
              : {
                  practiceName: formData.practiceName,
                  practiceAddress: formData.practiceAddress,
                  practiceCity: formData.practiceCity,
                  practiceState: formData.practiceState,
                  practiceZip: formData.practiceZip,
                  practicePhone: formData.practicePhone,
                }),
          }),
          ...(formData.role === "Researcher" && {
            institution: formData.institution,
            researchArea: formData.specialization,
            yearsExperience: formData.yearsExperience,
          }),
        },
      });

      console.log("✅ Signup successful:", data);
      setSubmitSuccess(true);

      // Show success message based on role
      setTimeout(() => {
        if (formData.role === "Patient") {
          // Patient can login immediately
          loginWithRedirect({
            authorizationParams: {
              screen_hint: "login",
              login_hint: formData.email,
            },
          });
        } else {
          // Provider/Researcher need to wait for approval
          navigate("/signup-pending");
        }
      }, 2000);
    } catch (error) {
      console.error("❌ Signup error:", error);

      // Extract error message from response
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "An error occurred during signup. Please try again.";

      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Typography variant="h6" gutterBottom>
              Create Your Account
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleChange("firstName")}
                error={!!errors.firstName}
                helperText={errors.firstName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange("lastName")}
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
            </Box>

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
              error={!!errors.email}
              helperText={errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange("password")}
              error={!!errors.password}
              helperText={
                errors.password ||
                "Min 8 characters, uppercase, lowercase, number, special character"
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange("confirmPassword")}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Typography variant="h6" gutterBottom>
              Select Your Role
            </Typography>

            {errors.role && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.role}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {roles.map((role) => (
                <Paper
                  key={role.value}
                  elevation={formData.role === role.value ? 4 : 1}
                  sx={{
                    p: 3,
                    cursor: "pointer",
                    border: 2,
                    borderColor:
                      formData.role === role.value
                        ? `${role.color}.main`
                        : "transparent",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      elevation: 4,
                      transform: "translateY(-2px)",
                    },
                  }}
                  onClick={() => {
                    setFormData({ ...formData, role: role.value });
                    setErrors({ ...errors, role: "" });
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        bgcolor: `${role.color}.main`,
                        color: "white",
                        p: 1.5,
                        borderRadius: 2,
                        display: "flex",
                      }}
                    >
                      {role.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="h6">{role.label}</Typography>
                        {role.requiresVerification && (
                          <Chip
                            label="Requires Verification"
                            size="small"
                            color="warning"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {role.description}
                      </Typography>
                    </Box>
                    {formData.role === role.value && (
                      <CheckCircle color={role.color} />
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        );

      case 2:
        // Verification step (only for Provider/Researcher)
        if (formData.role === "Patient") {
          return (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CheckCircle
                sx={{ fontSize: 80, color: "success.main", mb: 2 }}
              />
              <Typography variant="h5" gutterBottom>
                Almost Done!
              </Typography>
              <Typography color="text.secondary" paragraph>
                As a patient, you can start using NephroTrax immediately
                after signing up.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click "Complete Signup" to create your account.
              </Typography>
            </Box>
          );
        }

        if (formData.role === "Provider") {
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Typography variant="h6" gutterBottom>
                Provider Verification
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                Your credentials will be verified by our team. You'll receive an
                email once approved (typically within 24-48 hours).
              </Alert>

              <TextField
                fullWidth
                label="NPI Number"
                value={formData.npiNumber}
                onChange={handleChange("npiNumber")}
                error={!!errors.npiNumber}
                helperText={
                  errors.npiNumber || "10-digit National Provider Identifier"
                }
                placeholder="1234567890"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Badge color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Medical License Number"
                  value={formData.medicalLicense}
                  onChange={handleChange("medicalLicense")}
                  error={!!errors.medicalLicense}
                  helperText={errors.medicalLicense}
                />
                <FormControl fullWidth error={!!errors.licenseState}>
                  <InputLabel>License State</InputLabel>
                  <Select
                    value={formData.licenseState}
                    onChange={handleChange("licenseState")}
                    label="License State"
                  >
                    {usStates.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                label="Specialization"
                value={formData.specialization}
                onChange={handleChange("specialization")}
                error={!!errors.specialization}
                helperText={errors.specialization}
                placeholder="e.g., Hematology, Cardiology"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Autocomplete
                freeSolo
                options={practiceOptions}
                loading={searchingPractices}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : `${option.name}${option.city ? ` — ${option.city}, ${option.state}` : ""}`
                }
                inputValue={practiceInputValue}
                onInputChange={(event, newInputValue) => {
                  setPracticeInputValue(newInputValue);
                  // Typing after having picked an existing practice clears
                  // that selection — the new text is treated as a fresh
                  // (possibly new) practice name until they pick again.
                  if (formData.practiceId) {
                    setFormData({ ...formData, practiceId: null, practiceName: newInputValue });
                  } else {
                    setFormData({ ...formData, practiceName: newInputValue });
                  }
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === "object") {
                    setFormData({ ...formData, practiceId: newValue.id, practiceName: newValue.name });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Practice"
                    error={!!errors.practiceName}
                    helperText={
                      errors.practiceName ||
                      "Search for your practice, or type a new one to request it"
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {formData.practiceName && !formData.practiceId && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Alert severity="info" sx={{ mb: 0 }}>
                    "{formData.practiceName}" isn't in our list yet — it'll be reviewed
                    by our team alongside your credentials. Address details below are
                    optional but help us verify it faster.
                  </Alert>
                  <TextField
                    fullWidth
                    label="Practice Address (optional)"
                    value={formData.practiceAddress}
                    onChange={handleChange("practiceAddress")}
                  />
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.practiceCity}
                      onChange={handleChange("practiceCity")}
                    />
                    <FormControl fullWidth>
                      <InputLabel>State</InputLabel>
                      <Select
                        value={formData.practiceState}
                        onChange={handleChange("practiceState")}
                        label="State"
                      >
                        {usStates.map((state) => (
                          <MenuItem key={state} value={state}>
                            {state}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Zip"
                      value={formData.practiceZip}
                      onChange={handleChange("practiceZip")}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Practice Phone (optional)"
                    value={formData.practicePhone}
                    onChange={handleChange("practicePhone")}
                  />
                </Box>
              )}
            </Box>
          );
        }

        if (formData.role === "Researcher") {
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Typography variant="h6" gutterBottom>
                Researcher Verification
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }}>
                Your institutional affiliation will be verified. You'll receive
                approval within 24-48 hours.
              </Alert>

              <TextField
                fullWidth
                label="Institution/Organization"
                value={formData.institution}
                onChange={handleChange("institution")}
                error={!!errors.institution}
                helperText={errors.institution}
                placeholder="e.g., Johns Hopkins University"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <School color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Research Area"
                value={formData.specialization}
                onChange={handleChange("specialization")}
                error={!!errors.specialization}
                helperText={errors.specialization}
                placeholder="e.g., Amyloidosis, Clinical Trials"
              />

              <TextField
                fullWidth
                label="Years of Experience"
                type="number"
                value={formData.yearsExperience}
                onChange={handleChange("yearsExperience")}
                placeholder="5"
              />
            </Box>
          );
        }

        return null;

      default:
        return null;
    }
  };

  if (submitSuccess) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
          <CheckCircle sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            {formData.role === "Patient"
              ? "Account Created!"
              : "Application Submitted!"}
          </Typography>
          <Typography color="text.secondary" paragraph>
            {formData.role === "Patient"
              ? "Redirecting you to login..."
              : "Your credentials are being verified. You'll receive an email once approved (typically within 24-48 hours)."}
          </Typography>
          {loading && <CircularProgress sx={{ mt: 2 }} />}
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Join NephroTrax
          </Typography>
          <Typography color="text.secondary">
            Where Insight Meets Care
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        {/* Step Content */}
        <Box sx={{ minHeight: 400 }}>{renderStepContent(activeStep)}</Box>

        {/* Navigation Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          <Box sx={{ display: "flex", gap: 2 }}>
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Complete Signup"}
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Login Link */}
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{" "}
            <Link
              component="button"
              variant="body2"
              onClick={() => loginWithRedirect()}
              sx={{ cursor: "pointer" }}
            >
              Sign In
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}